import { OptionalCardLink } from '@playbooks/molecules/cards/card-link';
import { Skeleton } from '@playbooks/molecules/skeletons';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

import type { CardContentProps, CardNavigationProps } from './types';

type DisplayCardProps = Pick<
	CardContentProps,
	| 'badges'
	| 'className'
	| 'footer'
	| 'icon'
	| 'loading'
	| 'media'
	| 'meta'
	| 'photo'
	| 'pretitle'
	| 'subtitle'
	| 'text'
	| 'title'
> &
	CardNavigationProps;

export const DisplayCard = ({
	icon,
	photo,
	media,
	badges,
	pretitle,
	title,
	subtitle,
	text,
	meta,
	href,
	target,
	linkProps,
	loading,
	className = '',
	footer,
}: DisplayCardProps) => {
	// Theme
	const rootClassName = cn(
		'flex-column relative h-full gap-0 py-0 hover:border-border-strong hover:bg-surface-card-hover',
		className,
	);

	// Render
	if (loading) return <Skeleton layout='display' className={className} />;

	return (
		<Card className={rootClassName}>
			{badges && <div className='flex-end absolute top-4 right-4 gap-2'>{badges}</div>}
			<OptionalCardLink
				variant='ghost'
				aria-label={String(title)}
				href={href}
				target={target}
				{...linkProps}
				className='w-fit h-auto p-0'>
				{media || (
					<CardHeader className='aspect-[1/1] p-0 rounded-control flex-middle overflow-hidden m-4 w-14 bg-surface-hover text-foreground-body'>
						{photo ? (
							<img src={photo} alt={title + ' photo'} className='h-full w-full object-cover' />
						) : (
							<CardMedia className='h-full w-full'>
								<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
							</CardMedia>
						)}
					</CardHeader>
				)}
			</OptionalCardLink>
			<CardContent className='grow space-y-1 px-4 pb-4'>
				{pretitle && <div>{pretitle}</div>}
				{title && (
					<CardTitle>
						<OptionalCardLink
							variant='ghost'
							href={href}
							target={target}
							{...linkProps}
							className='hover:underline h-auto p-0'>
							{title}
						</OptionalCardLink>
					</CardTitle>
				)}
				{subtitle && <CardDescription className='line-clamp-2'>{subtitle}</CardDescription>}
				{text && <CardDescription className='line-clamp-2'>{text}</CardDescription>}
				{meta && <span className='line-clamp-1'>{meta}</span>}
			</CardContent>
			{footer && <CardFooter>{footer}</CardFooter>}
		</Card>
	);
};

export type { DisplayCardProps };
